// apps/main/src/clark-widget/CapacityModal.jsx — RUN AB, §7. The capacity refusal.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 WHY THIS IS NOT PurchaseResultModal, HAVING TRIED TO MAKE IT ONE.
//
// RUN AB's brief says to reuse PurchaseResultModal if it fits and to say why if it does not. It
// does not fit, for four reasons, three of which are in its own header:
//
//   1. IT CANNOT BE DISMISSED BY ESCAPE, ON PURPOSE. Its header: "🔴 DISMISSED ONLY BY ITS X. No
//      scrim-click-to-close, no Escape. A completed-or-failed purchase is a fact the customer
//      should have to actively acknowledge." RUN AB §7 requires Escape to dismiss. That is a
//      direct contradiction, and the purchase modal is RIGHT for its own case — a capacity notice
//      is not a receipt, and trapping a stranger in it is the opposite of the intent.
//   2. IT HAS NO SLOT FOR A CHILD. Its props are { open, onClose, outcome, eyebrow, title, lines[] }
//      — `lines` is an array of strings rendered as <p>. There is nowhere to put the orb, which
//      §7 requires to be the real, shared-clock orb rather than a decoration.
//   3. `outcome` IS 'success' | 'failure' AND DRIVES A TICK OR A CROSS ICON. A capacity refusal is
//      neither; both icons would be wrong, and both would sit where the orb belongs.
//   4. IT CARRIES LegalLinks ON BOTH FACES, because (its header again) "this is the screen that
//      shows the outcome of taking money". No money is being taken here.
//
// 🔴 AND THE FENCE. PurchaseResultModal lives at packages/ui/react/PurchaseResultModal.jsx, which
// is RUN AA's territory for this run. Adding a `children` prop and an `escapable` flag to it is an
// edit across that fence, so it is REPORTED AND PROPOSED, not done. If AA (or a later run) adds
// those two props, this component should collapse into it — the proposal is in RUN AB's report.
//
// What IS reused is the IDIOM, which is the thing that actually keeps the surfaces from drifting:
// the same scrim geometry, the same panel width and radius, the same navy header block, the same
// DM Serif Display title, the same close-button metrics and focus ring. Read side by side, this
// and PurchaseResultModal are obviously the same product.
//
// ── THE TWO VARIANTS ──────────────────────────────────────────────────────────────────────────
// 🔴 'hourly' says "it resets shortly". 'daily' MAKES NO TIME PROMISE AT ALL. The daily window may
// be many hours from clearing, and naming the hour tells an abuser exactly when to come back. It
// would also be a promise a ROLLING window cannot keep — there is no instant at which a rolling
// 24h sum resets, so any time named would be wrong as well as unwise. The copy for both comes from
// the server (anonSpend.spendRefusal), so the string a visitor reads is the string the refusing
// module chose; `variant` here only selects presentation.
//
// ── COLOR ─────────────────────────────────────────────────────────────────────────────────────
// --cc-brand / --cc-on-brand / --cc-on-brand-muted, which RUN MG found already defined AS A PAIR
// in every theme — verified again here: all 10 theme blocks in apps/main/public/themes.css define
// all three. 🔴 --cc-brand-navy is NOT used and must not be: it is #7E1428 in verdict and #F2F1EE
// in acquittal, i.e. near-black-red in one theme and near-white in another, so a single foreground
// choice against it is unreadable in one of the two.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef } from "react";
import ModalOrb from "./ModalOrb.jsx";

const CSS = `
.ckw-cap-root{position:fixed;inset:0;z-index:10002}
.ckw-cap-scrim{position:absolute;inset:0;background:color-mix(in srgb, var(--cc-chrome) 62%, transparent);
  backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.ckw-cap-panel{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);width:min(440px,94vw);
  max-height:92vh;overflow-y:auto;background:var(--cc-card,#fff);border-radius:16px;
  box-shadow:var(--cc-ia-shadow-modal);display:flex;flex-direction:column;padding:0 0 30px;
  font-family:'DM Sans',system-ui,sans-serif}
.ckw-cap-header{background:var(--cc-brand);padding:24px 24px 30px;border-radius:16px 16px 0 0;position:relative;flex-shrink:0}
/* 🔴 44px, not the 30px PurchaseResultModal uses — §7 requires >=44px tap targets. The VISUAL box
   stays 30px so the two modals still read alike; the extra reach comes from padding on an
   otherwise transparent button, which is what makes a target big without making it look big. */
.ckw-cap-close{position:absolute;top:7px;right:7px;width:44px;height:44px;border:0;background:none;
  padding:0;cursor:pointer;display:flex;align-items:center;justify-content:center;border-radius:10px}
.ckw-cap-close span{width:30px;height:30px;border:.5px solid var(--cc-on-brand-muted);border-radius:8px;
  display:flex;align-items:center;justify-content:center;color:var(--cc-on-brand-muted);font-size:14px;line-height:1}
.ckw-cap-close:hover span{color:var(--cc-on-brand)}
.ckw-cap-close:focus-visible{outline:none;box-shadow:0 0 0 2px var(--cc-focus-halo-light,rgba(255,255,255,.55)),0 0 0 5px var(--cc-focus-halo-dark,rgba(255,255,255,.2))}
.ckw-cap-orbwrap{display:flex;justify-content:center;margin:4px 0 14px}
.ckw-cap-title{font-family:'DM Serif Display',Georgia,serif;font-size:22px;font-weight:400;
  color:var(--cc-on-brand);margin:6px 0 0;line-height:1.2;text-align:center}
.ckw-cap-body{display:flex;flex-direction:column;gap:10px;padding:24px 28px 4px}
.ckw-cap-line{font-size:14.5px;line-height:1.55;color:var(--cc-ink,#141a26);text-align:center;margin:0}
.ckw-cap-ctas{display:flex;justify-content:center;padding:18px 28px 0}
.ckw-cap-cta{min-height:44px;padding:11px 22px;border-radius:10px;border:1px solid var(--cc-brand);
  background:var(--cc-brand);color:var(--cc-on-brand);font-size:14.5px;font-weight:600;cursor:pointer;
  font-family:inherit}
.ckw-cap-cta:focus-visible{outline:2px solid var(--cc-brand);outline-offset:2px}
@media (prefers-reduced-motion: reduce){.ckw-cap-scrim{backdrop-filter:none;-webkit-backdrop-filter:none}}
`;
let _cssInjected = false;
function useCapacityCss() {
  useEffect(() => {
    if (_cssInjected || typeof document === "undefined") return;
    const el = document.createElement("style");
    el.dataset.ckwCap = "1";
    el.textContent = CSS;
    document.head.appendChild(el);
    _cssInjected = true;
  }, []);
}

/**
 * @param {object}  props
 * @param {boolean} props.open
 * @param {function} props.onClose         dismiss. 🔴 MUST NOT NAVIGATE — see the note below.
 * @param {'hourly'|'daily'} props.variant hourly promises a short reset; daily promises nothing.
 * @param {string}  props.title            from the server's refusal body.
 * @param {string}  props.reply            from the server's refusal body.
 * @param {object}  props.returnFocusRef   the composer input. Focus goes back to it on close.
 */
export default function CapacityModal({ open, onClose, variant = "hourly", title, reply, returnFocusRef }) {
  useCapacityCss();
  const panelRef = useRef(null);
  const closeRef = useRef(null);

  // 🔴 ESCAPE DISMISSES, AND FOCUS IS TRAPPED WHILE OPEN AND RETURNED ON CLOSE.
  //
  // The return target is the COMPOSER, not document.body: §7 requires the visitor to be left on the
  // homepage "with the composer where it was", and a modal that drops focus to the top of the
  // document has moved them even though the pixels did not change.
  //
  // 🔴 DISMISSING MUST NOT NAVIGATE. There is deliberately no href, no location assignment and no
  // router call anywhere in this file — the only thing onClose does is unmount this component.
  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    // Focus the close button, not the panel: it is the one control, and it is what a screen reader
    // should land on.
    const t = setTimeout(() => closeRef.current?.focus(), 0);

    const onKeyDown = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose?.(); return; }
      if (e.key !== "Tab") return;
      // The trap. Only focusable elements INSIDE the panel are reachable while it is open.
      const focusables = panelRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKeyDown, true);
      // Return focus to the composer if the caller named one, else to whatever had it before.
      const target = returnFocusRef?.current || previouslyFocused;
      try { target?.focus?.(); } catch { /* the node may be gone — never fatal */ }
    };
  }, [open, onClose, returnFocusRef]);

  if (!open) return null;

  return (
    <div className="ckw-cap-root" data-testid="clark-capacity-modal" data-variant={variant}>
      {/* The scrim is NOT clickable-to-close. Escape and the X are the two doors, which keeps a
          stray click on a blurred homepage from dismissing a message the visitor has not read. */}
      <div className="ckw-cap-scrim" />
      <div className="ckw-cap-panel" ref={panelRef} role="dialog" aria-modal="true"
           aria-labelledby="ckw-cap-title">
        <div className="ckw-cap-header">
          <button type="button" className="ckw-cap-close" onClick={onClose}
                  aria-label="Dismiss" ref={closeRef} data-testid="clark-capacity-close">
            <span aria-hidden="true">✕</span>
          </button>
          <div className="ckw-cap-orbwrap">
            {/* 🔴 THE REAL ORB, ON THE PAGE'S SHARED CLOCK. Clicking it pulses EVERY registered
                orb — including the homepage orb blurred behind this scrim. See ModalOrb.jsx. */}
            <ModalOrb size={72} />
          </div>
          <h2 className="ckw-cap-title" id="ckw-cap-title">{title || "Clark is at capacity"}</h2>
        </div>
        <div className="ckw-cap-body">
          <p className="ckw-cap-line" data-testid="clark-capacity-reply">{reply}</p>
        </div>
        <div className="ckw-cap-ctas">
          {/* ONE control, and it is "close". 🔴 NOT the upgrade prompt (RUN WV found that the worst
              part of the old failure) and not a sign-in prompt either: signing in does not create
              capacity, because the ceiling is ours and it is global. */}
          <button type="button" className="ckw-cap-cta" onClick={onClose}
                  data-testid="clark-capacity-dismiss">Got it</button>
        </div>
      </div>
    </div>
  );
}
